import type { Registry } from "@/api.js"
import { Flag } from "@/components/Flag.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { FieldControl } from "./Field.js"
import { type EditorProviders, laneTitle } from "./model.js"

/**
 * What sources are installed, and what each one costs to ask.
 *
 * A rule set is only half of what the system runs: a rule's lane, the order
 * things are asked in and whether a finding costs money are all properties of
 * the provider behind it. Without this a source could be installed and
 * invisible here.
 *
 * The cost word comes from `laneTitle`, so this list and the rule grid cannot
 * describe the same band differently.
 *
 * **The settings became real controls on 2026-09-24, and were a read-only
 * preview for as long as they were not.** Nothing parsed a provider section of
 * the document and every provider took its options at construction, so a
 * control would have needed a restart — and a knob that does nothing is worse
 * than an absent one. What changed is the other end: the parser reads the
 * section, `IntegrationEnvironment` hands a provider its settings per call, and
 * a publish therefore reaches the next decision like every other setting on
 * this screen.
 */
export function ProviderList({
  registry,
  providers,
  onChange,
}: {
  registry: Registry
  providers: EditorProviders
  onChange: (next: EditorProviders) => void
}) {
  const anyFields = registry.providers.some((provider) => provider.fields.length > 0)

  return (
    <Table>
      <thead>
        <tr>
          <Th>Źródło</Th>
          <Th className="w-24">Kraje</Th>
          <Th className="w-32">Koszt</Th>
          <Th>Reguły</Th>
          {anyFields && <Th className="w-72">Ustawienia</Th>}
        </tr>
      </thead>
      <tbody>
        {registry.providers.map((provider) => {
          const fed = registry.rules.filter((rule) => rule.dependsOn.includes(provider.providerId))

          return (
            <Tr key={provider.providerId}>
              <Td className="py-2">
                <span className="font-medium text-foreground" title={provider.description}>
                  {provider.title}
                </span>
                <span className="ml-2 font-mono text-[12px] text-muted-foreground">
                  {provider.providerId}
                </span>
              </Td>

              <Td className="py-2 font-mono text-[12.5px] text-muted-foreground">
                {provider.countries === "all" ? (
                  "wszystkie"
                ) : (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {provider.countries.map((code) => (
                      <span key={code} className="flex items-center gap-1">
                        <Flag code={code} />
                        {code}
                      </span>
                    ))}
                  </span>
                )}
              </Td>

              {/* Not decoration: the cost is what fixes the lane of every rule
                  that reads this source. */}
              <Td className="py-2 text-[13px] text-muted-foreground">
                {laneTitle(provider.worstCaseCost)}
              </Td>

              <Td className="py-2 font-mono text-[12.5px]">
                {fed.length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  fed.map((rule) => rule.ruleId).join(" · ")
                )}
              </Td>

              {anyFields && (
                <Td className="py-2">
                  {provider.fields.length === 0 ? (
                    <span className="text-[13px] text-muted-foreground">—</span>
                  ) : (
                    <div className="space-y-2.5">
                      {provider.fields.map((field) => {
                        const id = `${provider.providerId}-${field.key}`
                        return (
                          <div key={field.key} className="space-y-1">
                            <label
                              htmlFor={id}
                              className="block text-[12.5px] leading-4 text-muted-foreground"
                            >
                              {field.label}
                            </label>
                            <FieldControl
                              id={id}
                              field={field}
                              value={providers[provider.providerId]?.[field.key]}
                              onChange={(next) =>
                                onChange({
                                  ...providers,
                                  [provider.providerId]: {
                                    ...providers[provider.providerId],
                                    [field.key]: next,
                                  },
                                })
                              }
                            />
                            {field.help !== undefined && (
                              <p className="max-w-prose text-[12px] leading-4 text-muted-foreground">
                                {field.help}
                              </p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Td>
              )}
            </Tr>
          )
        })}
      </tbody>
    </Table>
  )
}
